import {
  OrderType,
  SearchLearningObjectiveStatementsDocument,
  useGetLearningObjectiveStatementByIdQuery,
  useUpdateLearningObjectiveStatementMutation,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  DialogActions,
  Button,
  Loading,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { FALLBACK_LEVEL_NAME } from '../../constants';
import { getIsNameAlreadyExistsError, getQuestionSourceForLOS } from '../../utils';
import { LOSInfoSection } from '../los-info-section';
import { LOSInput } from '../los-input';

import { EditLOSFormInput, type EditLOSFormValues } from './edit-los-schema';

interface EditLOSFormModalProps {
  losId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function EditLOSFormModal(props: Readonly<EditLOSFormModalProps>) {
  const { losId, open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const formContext = useForm<EditLOSFormValues>({
    resolver: zodResolver(EditLOSFormInput),
  });
  const {
    formState: { isSubmitting },
    reset,
    setError,
  } = formContext;

  const { qBankStructureInformation, qBankStructureLevelItems } = useQBankStructure();
  const { id: qBankStructureId, code } = qBankStructureInformation;

  const [updateLOS] = useUpdateLearningObjectiveStatementMutation();

  const searchLOSVariables = useMemo(
    () => ({
      order: { field: 'updatedAt', type: OrderType.Asc },
      paging: { currentPage: 0, pageSize: 9999 },
      search: { qBankStructureId: qBankStructureId },
    }),
    [qBankStructureId],
  );

  const { data: losData, loading: isLoading } = useGetLearningObjectiveStatementByIdQuery({
    variables: {
      input: { id: losId ?? '' },
    },
    skip: !losId,
  });

  const los = useMemo(() => {
    if (!losId || !losData) return null;
    return losData.qbsGetLearningObjectiveStatementById ?? null;
  }, [losId, losData]);

  const chapterId = los?.qBankStructureLevelItem?.id ?? '';

  const chapterName = useMemo(() => {
    if (!chapterId) return FALLBACK_LEVEL_NAME;
    const levelItem = qBankStructureLevelItems.find((item) => item.id === chapterId);
    return levelItem?.tag?.name ?? FALLBACK_LEVEL_NAME;
  }, [chapterId, qBankStructureLevelItems]);

  const qSource = useMemo(() => {
    return getQuestionSourceForLOS(code, qBankStructureLevelItems, chapterId);
  }, [code, qBankStructureLevelItems, chapterId]);

  const handleClose = () => {
    onClose();
  };

  const handleResetForm = () => {
    reset();
  };

  useEffect(() => {
    if (los) {
      formContext.reset({ los: los.tag.name });
    }
  }, [los]);

  const onSubmit = async (formValues: EditLOSFormValues) => {
    if (!losId || !chapterId) {
      logger.error('LOS ID or Chapter ID is not provided. Unable to update LOS.');
      showSnackbar({
        message: 'Unable to save LOS',
        variant: 'error',
      });
      return;
    }

    if (losId && chapterId) {
      await updateLOS({
        variables: {
          input: {
            qBankStructureId: qBankStructureId,
            chapterLevelItemId: chapterId,
            learningObjectiveStatementId: losId,
            name: formValues.los.trim(),
          },
        },
        refetchQueries: [
          {
            query: SearchLearningObjectiveStatementsDocument,
            variables: searchLOSVariables,
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: async () => {
          handleClose();
          showSnackbar({
            message: 'LOS has been saved',
            variant: 'success',
          });
        },
        onError: (error) => {
          if (getIsNameAlreadyExistsError(error)) {
            setError('los', { message: 'ข้อมูลซ้ำกับวัตถุประสงค์การเรียนรู้ที่มีอยู่แล้วในบทนี้' });
          } else {
            showSnackbar({
              message: 'Unable to save LOS',
              variant: 'error',
            });
          }
        },
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='lg'
      slotProps={{
        paper: {
          component: Form,
          onSubmit: formContext.handleSubmit(onSubmit),
          sx: { maxHeight: '775px' },
        },
        transition: {
          onEnter: handleResetForm,
          onExited: handleResetForm,
        },
      }}
    >
      <FormProvider {...formContext}>
        <DialogTitle onClose={handleClose} loading={isSubmitting}>
          Edit LOS
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
        >
          {isLoading ? (
            <Loading sx={{ minHeight: '257px' }} />
          ) : (
            <>
              <LOSInfoSection
                name={chapterName}
                qSource={qSource}
                createdAt={los?.createdAt}
                updatedAt={los?.updatedAt}
              />
              <LOSInput
                name='los'
                label='วัตถุประสงค์การเรียนรู้'
                helperText='สามารถแก้ไขวัตถุประสงค์ได้ข้อเดียวเท่านั้น'
                preventNewLine
              />
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            disabled={isSubmitting}
            onClick={handleClose}
            color='secondary'
            variant='outlined'
            size='large'
          >
            Cancel
          </Button>
          <Button type='submit' size='large' loading={isSubmitting}>
            Save
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
